/**
 * Public QR crew-join page — /join/:token (WS: QR crew signup).
 *
 * The page a student lands on after scanning the org's QR code at a career
 * fair. Mobile-first, minimal friction: org name + blurb, then either
 * "Continue with Google" (one tap, done) or a four-field form. A visitor who
 * is already signed in gets a one-tap join card; a visitor whose email
 * already has an account is routed to sign in and returned here.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { rememberOrgSlugForNextLoad } from '../../context/OrganizationContext';
import {
  getJoinInfo,
  joinWithPassword,
  joinWithGoogle,
  joinAsMember,
  type JoinInfo,
  type JoinResponse,
} from '../../api/crewInvites';
import { getErrorMessage } from '../../api/client';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import Logo from '../../components/brand/Logo';

const PASSWORD_RULES: { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw) => /[0-9]/.test(pw) },
];

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-paper flex flex-col items-center px-4 py-10">
      <div className="mb-6">
        <Logo size={18} on="light" wordmarkSize={24} />
      </div>
      <div className="w-full max-w-md bg-white p-6 border border-hairline">{children}</div>
      <p className="mt-6 text-center text-xs text-gray-500">
        <Link to="/privacy" className="underline hover:text-gray-700">Privacy</Link>
        {' · '}
        <Link to="/terms" className="underline hover:text-gray-700">Terms</Link>
      </p>
    </div>
  );
}

export default function JoinPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [info, setInfo] = useState<JoinInfo | null>(null);
  const [dead, setDead] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ACCOUNT_EXISTS: the email already has an account — show the sign-in route.
  const [existingEmail, setExistingEmail] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    getJoinInfo(token)
      .then(setInfo)
      .catch(() => setDead(true));
  }, [token]);

  const finish = async (res: JoinResponse) => {
    // Land in the org they JUST joined — without this, a returning
    // multi-org user is restored to whatever org localStorage remembered
    // from a previous session (found by a real two-org QR signup).
    rememberOrgSlugForNextLoad(res.joinedOrgSlug);
    await refreshUser();
    navigate('/crew', { replace: true });
  };

  const handleGoogle = async (idToken: string) => {
    setError(null);
    setBusy(true);
    try {
      const res = await joinWithGoogle(token, idToken);
      await finish(res);
    } catch (err) {
      setError(getErrorMessage(err));
      setBusy(false);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!PASSWORD_RULES.every((r) => r.test(password))) {
      setError('Please meet all the password requirements.');
      return;
    }
    setBusy(true);
    try {
      const res = await joinWithPassword(token, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
      });
      await finish(res);
    } catch (err) {
      const data = (err as { response?: { data?: { code?: string } } }).response?.data;
      if (data?.code === 'ACCOUNT_EXISTS') {
        setExistingEmail(email.trim());
      } else {
        setError(getErrorMessage(err));
      }
      setBusy(false);
    }
  };

  const handleOneTap = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await joinAsMember(token);
      await finish(res);
    } catch (err) {
      setError(getErrorMessage(err));
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------------------

  if (dead) {
    return (
      <Shell>
        <div className="text-center">
          <div className="w-14 h-14 bg-yellow-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">This signup link isn't active</h1>
          <p className="text-gray-600 text-sm">
            It may have expired or been replaced. Ask the organization for their current QR code
            or signup link.
          </p>
        </div>
      </Shell>
    );
  }

  if (!info) {
    return (
      <Shell>
        <div className="h-32 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
      </Shell>
    );
  }

  // Email already registered — route through login and back here.
  if (existingEmail) {
    return (
      <Shell>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">You already have an account</h1>
        <p className="text-gray-600 text-sm mb-4">
          <span className="font-medium">{existingEmail}</span> is already registered with
          ProgramRow. Sign in and you'll be brought right back here to join{' '}
          <span className="font-medium">{info.orgName}</span> with one tap.
        </p>
        <button
          type="button"
          onClick={() => navigate('/login', { state: { from: { pathname: `/join/${token}` } } })}
          className="w-full px-4 py-2.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
        >
          Sign in to join
        </button>
        <button
          type="button"
          onClick={() => setExistingEmail(null)}
          className="mt-3 w-full text-sm text-gray-500 hover:text-gray-700"
        >
          Use a different email
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center mb-5">
        <p className="text-xs font-medium uppercase tracking-wide text-primary-600 mb-1">
          Crew signup
        </p>
        <h1 className="text-2xl font-bold text-gray-900">{info.orgName}</h1>
        <p className="mt-2 text-sm text-gray-600">{info.blurb}</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
          {error}
        </div>
      )}

      {user ? (
        // Already signed in — one tap.
        <div className="text-center">
          <p className="text-sm text-gray-700 mb-4">
            Join as{' '}
            <span className="font-medium">
              {user.firstName} {user.lastName}
            </span>{' '}
            ({user.email})?
          </p>
          <button
            type="button"
            onClick={handleOneTap}
            disabled={busy}
            className="w-full px-4 py-2.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Joining…' : `Join ${info.orgName} as crew`}
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={handlePassword} className="space-y-3" noValidate>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                required
                autoComplete="given-name"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rack focus:border-rack"
              />
              <input
                type="text"
                required
                autoComplete="family-name"
                placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rack focus:border-rack"
              />
            </div>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rack focus:border-rack"
            />
            <div>
              <input
                type="password"
                required
                autoComplete="new-password"
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-rack focus:border-rack"
              />
              {password.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {PASSWORD_RULES.map((rule) => {
                    const met = rule.test(password);
                    return (
                      <li
                        key={rule.label}
                        className={`text-xs flex items-center gap-1.5 ${met ? 'text-green-600' : 'text-gray-400'}`}
                      >
                        <span aria-hidden="true">{met ? '✓' : '○'}</span>
                        {rule.label}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <button
              type="submit"
              disabled={busy || !firstName || !lastName || !email || !password}
              className="w-full px-4 py-2.5 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Joining…' : `Join ${info.orgName}`}
            </button>
          </form>

          <GoogleSignInButton text="continue_with" onCredential={handleGoogle} />

          <p className="mt-4 text-center text-xs text-gray-500">
            By joining you agree to the{' '}
            <Link to="/terms" className="underline hover:text-gray-700">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="underline hover:text-gray-700">Privacy Policy</Link>.
          </p>
        </>
      )}
    </Shell>
  );
}
